import { sendUserActionEvents } from "@shared/api";
import { BASE_URL } from "../api";

const FLUSH_DELAY_MS = 800;
const RETRY_DELAY_MS = 30_000;
const MAX_QUEUE_SIZE = 200;
const BATCH_SIZE = 100;
const ACTION_TYPES = new Set([
  "page_view",
  "modal_open",
  "feature_launch",
  "button_click",
  "action",
]);

let activeUserId = null;
let queue = [];
let flushTimer = null;
let sending = false;

const storageKey = (userId) => `user-analytics-queue:${userId}`;

const createEventId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}-4000-8000-${Math.random()
    .toString(16)
    .slice(2, 14)}`;
};

const persistQueue = () => {
  if (!activeUserId || typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(storageKey(activeUserId), JSON.stringify(queue.slice(-MAX_QUEUE_SIZE)));
  } catch {
    // Analytics must not interfere with the main application when storage is unavailable.
  }
};

const loadQueue = (userId) => {
  if (!userId || typeof localStorage === "undefined") return [];
  try {
    const parsed = JSON.parse(localStorage.getItem(storageKey(userId)) || "[]");
    return Array.isArray(parsed) ? parsed.slice(-MAX_QUEUE_SIZE) : [];
  } catch {
    return [];
  }
};

const scheduleFlush = (delay = FLUSH_DELAY_MS) => {
  if (flushTimer || !activeUserId) return;
  flushTimer = window.setTimeout(() => {
    flushTimer = null;
    void flushUserAnalytics();
  }, delay);
};

export const configureUserAnalytics = (userId) => {
  const normalizedUserId = String(userId || "").trim() || null;
  if (normalizedUserId === activeUserId) return;
  if (flushTimer) window.clearTimeout(flushTimer);
  flushTimer = null;
  activeUserId = normalizedUserId;
  queue = activeUserId ? loadQueue(activeUserId) : [];
  if (queue.length) scheduleFlush(0);
};

export const trackUserAction = ({ actionType, actionName, pagePath, metadata = {} }) => {
  if (!activeUserId || !ACTION_TYPES.has(actionType)) return false;
  const normalizedName = String(actionName || "").trim();
  if (!normalizedName) return false;
  queue.push({
    event_id: createEventId(),
    action_type: actionType,
    action_name: normalizedName,
    platform: "web",
    page_path: String(pagePath || window.location?.pathname || "").slice(0, 512),
    metadata: metadata && typeof metadata === "object" && !Array.isArray(metadata) ? metadata : {},
    occurred_at: new Date().toISOString(),
  });
  queue = queue.slice(-MAX_QUEUE_SIZE);
  persistQueue();
  scheduleFlush();
  return true;
};

export async function flushUserAnalytics({ keepalive = false } = {}) {
  if (sending || !activeUserId || !queue.length || !navigator.onLine) return;
  const token = localStorage.getItem("accessToken");
  if (!token) return;

  const batch = queue.splice(0, BATCH_SIZE);
  persistQueue();
  sending = true;
  try {
    await sendUserActionEvents({
      baseUrl: BASE_URL,
      token,
      events: batch,
      keepalive,
    });
  } catch {
    queue = [...batch, ...queue].slice(-MAX_QUEUE_SIZE);
    persistQueue();
    scheduleFlush(RETRY_DELAY_MS);
  } finally {
    sending = false;
  }
  if (queue.length) scheduleFlush();
}

const CYRILLIC_MAP = {
  а: "a", б: "b", в: "v", г: "g", д: "d", е: "e", ё: "e", ж: "zh", з: "z",
  и: "i", й: "y", к: "k", л: "l", м: "m", н: "n", о: "o", п: "p", р: "r",
  с: "s", т: "t", у: "u", ф: "f", х: "h", ц: "ts", ч: "ch", ш: "sh", щ: "sch",
  ъ: "", ы: "y", ь: "", э: "e", ю: "yu", я: "ya",
};

export const normalizeAnalyticsName = (value, fallback = "unknown") => {
  const transliterated = String(value || "")
    .trim()
    .toLowerCase()
    .split("")
    .map((character) => CYRILLIC_MAP[character] ?? character)
    .join("");
  const normalized = transliterated
    .replace(/[^a-z0-9_.:-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^[_:.-]+|[_:.-]+$/g, "")
    .slice(0, 128);
  return normalized || fallback;
};

export const getPageAnalyticsName = (pathname) => {
  const path = String(pathname || "/").replace(/\/+$/, "") || "/";
  if (path === "/") return "home";
  const accountMatch = path.match(/^\/userlk\/[^/]+(?:\/(.*))?$/i);
  if (accountMatch) {
    const tail = accountMatch[1] || "profile";
    if (tail === "profile") return "account_profile";
    if (tail === "usertravelplans") return "account_travel_list";
    if (tail.startsWith("usertravelplans/")) return "account_travel_detail";
    if (tail === "usertravelmap") return "account_map";
    if (tail === "userstatistics") return "account_statistics";
    if (tail === "settings") return "account_settings";
    if (tail === "useradmin") return "admin_home";
    if (tail.startsWith("useradmin/")) return "admin_detail";
    return `account_${normalizeAnalyticsName(tail)}`;
  }
  if (/^\/(?:marshruty|publictravelplans)\//i.test(path)) return "public_travel_detail";
  if (/^\/travel\/shared\//i.test(path)) return "shared_travel";
  if (/^\/chto-posmotret\//i.test(path)) return "vacation_destination";
  return normalizeAnalyticsName(path.replace(/^\//, ""), "home");
};
